/*
 * H-19 · FILTRADO DEL COMBOBOX DE CUENTA (RF-178, RF-179, RN-549 a RN-553).
 *
 * EL FILTRADO ES EN EL CLIENTE, sobre la lista que la pantalla YA tiene: ni una
 * petición al servidor por pulsación y ninguna operación nueva de API (RNF-099).
 *
 * LO QUE ESTE MÓDULO NO HACE, y es contrato (RN-550): no filtra por propietario,
 * por tipo ni por moneda. La lista que recibe YA VIENE FILTRADA POR EL SERVIDOR
 * según el propósito de la consulta —origen, destino o ver—. Quien añada aquí un
 * filtro de propiedad duplica una regla de servidor en el cliente, que es
 * exactamente lo que el contrato prohíbe.
 */

import { formatearImporte } from '../../../nucleo/importes/formatoDeImporte.js';

/**
 * Misma normalización que ya rige la unicidad y el orden alfabético de las cuentas
 * en el servidor: sin distinguir mayúsculas, sin acentos y colapsando espacios
 * (RN-549). Escribir `nomina` encuentra «Nómina BBVA».
 */
const DIACRITICOS = new RegExp('[\\u0300-\\u036f]', 'g');

export function normalizar(texto) {
  return String(texto ?? '')
    .normalize('NFD')
    .replace(DIACRITICOS, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Filtra por coincidencia PARCIAL EN CUALQUIER POSICIÓN contra el nombre de la
 * cuenta y contra los cuatro últimos dígitos (RN-549). Escribir `1234` encuentra
 * la cuenta que termina en 1234.
 *
 * Con el texto vacío devuelve la lista completa y en su orden: el orden lo resolvió
 * el servidor y el cliente no reordena (D-29, RN-071).
 */
export function filtrarCuentas(cuentas, texto) {
  const buscado = normalizar(texto);
  if (!buscado) return [...(cuentas ?? [])];

  const soloDigitos = buscado.replace(/\D/g, '');

  return (cuentas ?? []).filter((cuenta) => {
    if (normalizar(cuenta?.nombre).includes(buscado)) return true;
    if (soloDigitos && String(cuenta?.cuatroUltimosDigitos ?? '').includes(soloDigitos)) return true;
    return false;
  });
}

/**
 * Cómo se lee cada opción: **nombre · tipo · ••1234 · importe**, más
 * «de [propietario]» cuando la cuenta es ajena —obligatorio en el selector de
 * destino de un traspaso— y «desactivada» cuando lo está.
 *
 * LO QUE YA SE MOSTRABA NO SE QUITA: los cuatro dígitos se AÑADEN. Una cuenta sin
 * dígitos capturados —el efectivo— NO MUESTRA EL APARTADO: ni «••», ni «••----»,
 * ni «sin dígitos». El efectivo es el caso normal, no una falta de datos (RN-544).
 */
export function describirCuenta(cuenta) {
  const partes = [cuenta?.nombreTipoCuenta].filter(Boolean);
  if (cuenta?.cuatroUltimosDigitos) partes.push(`••${cuenta.cuatroUltimosDigitos}`);

  const valor = cuenta?.esCredito ? cuenta?.deuda : cuenta?.saldo;
  if (valor !== null && valor !== undefined) partes.push(formatearImporte(valor, cuenta?.codigoISO));

  if (cuenta?.esPropia === false && cuenta?.nombrePropietario) partes.push(`de ${cuenta.nombrePropietario}`);
  if (cuenta?.activo === false) partes.push('desactivada');

  return partes.join(' · ');
}

/**
 * Lo mismo para el lector de pantalla. Los cuatro dígitos se anuncian como
 * «terminación 1234», no como viñetas (RNF-100).
 */
export function describirCuentaParaLector(cuenta) {
  const partes = [cuenta?.nombre, cuenta?.nombreTipoCuenta].filter(Boolean);
  if (cuenta?.cuatroUltimosDigitos) partes.push(`terminación ${cuenta.cuatroUltimosDigitos}`);

  const valor = cuenta?.esCredito ? cuenta?.deuda : cuenta?.saldo;
  if (valor !== null && valor !== undefined) partes.push(formatearImporte(valor, cuenta?.codigoISO));

  if (cuenta?.esPropia === false && cuenta?.nombrePropietario) partes.push(`de ${cuenta.nombrePropietario}`);
  if (cuenta?.activo === false) partes.push('cuenta desactivada');

  return partes.join(', ');
}

/** El número de coincidencias, para la región aria-live discreta (RNF-100). */
export function anunciarCoincidencias(cantidad) {
  if (cantidad === 0) return 'Ninguna cuenta coincide';
  return cantidad === 1 ? '1 cuenta coincide' : `${cantidad} cuentas coinciden`;
}
